/**
 * Clean, Simple Order Fees Hook
 * Pure calculations based on rules - NO localStorage dependencies
 * Single source of truth for all fee calculations
 */

import { useAppStore } from '@/store/useAppStore';
import { useCartStore, useCartSubtotal } from '@/store/useCartStore';
import { calculateDeliveryFee, getFreeDeliveryThreshold } from '@/utils/feeCalculations';

interface UseOrderFeesResult {
  /** Service fee based on rules */
  serviceFee: number;
  /** Delivery fee based on rules (estimated, not address-specific) */
  deliveryFee: number;
  /** Total amount */
  total: number;
  /** Free delivery threshold based on rules */
  freeDeliveryThreshold: number;
  /** Whether free delivery is achieved */
  hasFreeDelivery: boolean;
  /** Amount needed to reach free delivery */
  amountForFreeDelivery: number;
}

/**
 * Calculate all order fees based on current cart and restaurant rules
 * This is a pure, stateless calculation - no backend calls, no localStorage
 */
export function useOrderFees(): UseOrderFeesResult {
  const subtotal = useCartSubtotal();
  const orderType = useCartStore((state) => state.orderType);
  const promoDiscount = useCartStore((state) => state.promoDiscount);
  const restaurant = useAppStore((state) => state.restaurant);

  // Service fee is no longer customer-facing - it's deducted from restaurant deposit
  // Always return 0 for frontend display
  const serviceFee = 0;

  // Calculate delivery fee based on rules (estimated minimum)
  const deliveryFee = calculateDeliveryFee(subtotal, restaurant, orderType);

  // Get free delivery threshold from rules
  const freeDeliveryThreshold = getFreeDeliveryThreshold(restaurant);

  // Check if free delivery is achieved
  const hasFreeDelivery = orderType === 'delivery' && deliveryFee === 0;

  // Calculate amount needed for free delivery
  const amountForFreeDelivery = hasFreeDelivery ? 0 : Math.max(0, freeDeliveryThreshold - subtotal);

  // Calculate total (service fee is not included - it's a platform deduction from restaurant)
  const total = subtotal + deliveryFee - promoDiscount;

  return {
    serviceFee,
    deliveryFee,
    total,
    freeDeliveryThreshold,
    hasFreeDelivery,
    amountForFreeDelivery,
  };
}
